import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { EnvelopeDto } from '@lib/dto/envelope.dto'
import { LIVE_SIGNER_STATUSES, type SignerStatus } from '@lib/enums/signer-status.enum'
import { extendEnvelopeInputSchema, type ExtendEnvelopeInput } from '@lib/input/envelope.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import type { EnvelopeAccessValue } from '@/envelopes/EnvelopeAccess'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { localInputValue } from '@/helpers/datetime-local'
import { envelopeKeys } from '@/hooks/queryKeys'

const WEEK_MS = 7 * 24 * 60 * 60_000
const LIVE: ReadonlySet<SignerStatus> = new Set(LIVE_SIGNER_STATUSES)

/** A deadline the dialog can move: the envelope's, or a signer's own. */
export interface MovableDeadline {
  /** The signer's id, or null for the envelope's. */
  signerId: string | null
  label: string
  current: Date
}

/** The envelope's deadline, then the own deadline of each signer who hasn't finished. */
export function movableDeadlines(envelope: EnvelopeDto): MovableDeadline[] {
  const signers = envelope.signers.flatMap((signer) =>
    signer.expiresAt && LIVE.has(signer.status)
      ? [{ signerId: signer.id, label: `${signer.name}’s deadline`, current: new Date(signer.expiresAt) }]
      : [],
  )
  return [{ signerId: null, label: signers.length > 0 ? 'Envelope deadline' : 'New deadline', current: new Date(envelope.expiresAt) }, ...signers]
}

/**
 * The extend body for the `datetime-local` values, one per deadline, or what's
 * wrong with each. A value left at the current deadline isn't sent. The server
 * checks the same rules, and also how signer deadlines sit with each other and
 * the envelope's; these are the ones the person can fix field by field.
 */
export function checkDeadlines(
  values: readonly string[],
  deadlines: readonly MovableDeadline[],
  now: Date,
): { input: ExtendEnvelopeInput } | { problems: (string | undefined)[] } {
  const moved: { deadline: MovableDeadline; at: Date }[] = []
  const problems = deadlines.map((deadline, index) => {
    const value = values[index] ?? ''
    if (value === localInputValue(deadline.current)) return undefined
    const at = value ? new Date(value) : null
    if (!at || Number.isNaN(at.getTime())) return 'Pick a date and time.'
    if (at <= now) return 'Pick a time in the future.'
    if (at <= deadline.current) return `Pick a time after the current deadline, ${deadline.current.toLocaleString()}.`
    moved.push({ deadline, at })
    return undefined
  })
  if (problems.some(Boolean) || moved.length === 0) return { problems }
  const envelopeAt = moved.find((item) => item.deadline.signerId === null)?.at
  const signers = moved.flatMap(({ deadline, at }) =>
    deadline.signerId ? [{ id: deadline.signerId, expiresAt: at.toISOString() }] : [],
  )
  const parsed = extendEnvelopeInputSchema.safeParse({
    ...(envelopeAt ? { expiresAt: envelopeAt.toISOString() } : {}),
    ...(signers.length > 0 ? { signers } : {}),
  })
  return parsed.success ? { input: parsed.data } : { problems: deadlines.map(() => 'Pick a date and time.') }
}

interface ExtendDeadlineFormProps {
  envelope: EnvelopeDto
  access: Pick<EnvelopeAccessValue, 'service' | 'cacheKey'>
}

/** Moves a pending envelope's deadline later, and signers' own. */
export function ExtendDeadlineDialog({
  open,
  onOpenChange,
  ...form
}: ExtendDeadlineFormProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <ExtendDeadlineForm {...form} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

/**
 * Lives inside the dialog content so each opening starts fresh: a week past the
 * envelope's deadline when that's the only one, else every deadline as it is.
 */
function ExtendDeadlineForm({ envelope, access, onDone }: ExtendDeadlineFormProps & { onDone: () => void }) {
  const queryClient = useQueryClient()
  const deadlines = movableDeadlines(envelope)
  const [values, setValues] = React.useState(() =>
    deadlines.length === 1
      ? deadlines.map((deadline) => localInputValue(new Date(Math.max(deadline.current.getTime(), Date.now()) + WEEK_MS)))
      : deadlines.map((deadline) => localInputValue(deadline.current)),
  )
  const checked = checkDeadlines(values, deadlines, new Date())

  const extend = useMutation({
    mutationFn: (input: ExtendEnvelopeInput) => access.service.extend(envelope.id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(envelopeKeys.detail(access.cacheKey, envelope.id), data)
      void queryClient.invalidateQueries({ queryKey: envelopeKeys.audit(access.cacheKey, envelope.id) })
      void queryClient.invalidateQueries({ queryKey: [...envelopeKeys.scope(access.cacheKey), 'list'] })
      toast.success('Deadline extended')
      onDone()
    },
  })

  const problems = 'problems' in checked ? checked.problems : []

  return (
    <>
      <DialogHeader>
        <DialogTitle>Extend the deadline</DialogTitle>
        <DialogDescription>
          {deadlines.length === 1
            ? 'Signers who haven’t finished get until the new deadline.'
            : 'Move any of these later. A signer’s own deadline can’t be after the envelope’s, or before that of a signer earlier in the order.'}{' '}
          Nobody is emailed about the change; later reminders show the new deadline.
        </DialogDescription>
      </DialogHeader>
      {deadlines.map((deadline, index) => (
        <Field
          key={deadline.signerId ?? 'envelope'}
          label={deadline.label}
          htmlFor={`extend-deadline-${String(index)}`}
          hint={`Currently ${deadline.current.toLocaleString()}.`}
          error={problems[index]}
        >
          <Input
            id={`extend-deadline-${String(index)}`}
            type="datetime-local"
            value={values[index] ?? ''}
            onChange={(event) => {
              const next = event.target.value
              setValues((current) => current.map((value, at) => (at === index ? next : value)))
            }}
          />
        </Field>
      ))}
      {extend.isError ? <p className="text-body text-danger">{apiErrorMessage(extend.error)}</p> : null}
      <DialogFooter>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={extend.isPending || !('input' in checked)}
          onClick={() => {
            if ('input' in checked) extend.mutate(checked.input)
          }}
        >
          {extend.isPending ? <Loader2 className="animate-spin" /> : null}
          Extend deadline
        </Button>
      </DialogFooter>
    </>
  )
}
